/**
 * Shared animation utilities used by Animation and AnimationSequence.
 */

import { Scene } from "@babylonjs/core/scene";
import { Node } from "@babylonjs/core/node";
import { Bone } from "@babylonjs/core/Bones/bone";
import { TransformNode } from "@babylonjs/core/Meshes/transformNode";
import { AssetContainer } from "@babylonjs/core/assetContainer";
import { LoadAssetContainerAsync } from "@babylonjs/core/Loading/sceneLoader";
import { Animation as BabylonAnimation } from "@babylonjs/core/Animations/animation";
import type { AnimationGroup } from "@babylonjs/core/Animations/animationGroup";
import { Vector3, Quaternion } from "@babylonjs/core/Maths/math.vector";
import "@babylonjs/loaders/glTF";
import { ResourceManager } from "../loaders/ResourceManager";
import { Logger } from "./Logger";
import type { TransformKeyframe } from "../scenedata/behaviors/transformkeyframe";

/**
 * Resolve a TargetedAnimation's target to the Node it effectively animates.
 * Bones aren't part of the scene graph (`Node.getDescendants()` never
 * returns them), so a Bone target must be resolved to its linked
 * TransformNode before it can be tested against a node set.
 *
 * `Bone extends Node`, so this check must come before the `instanceof Node`
 * fallback — otherwise every Bone matches the Node branch and resolves to
 * itself instead of its linked TransformNode.
 */
export function resolveAnimationTargetNode(target: unknown): Node | null {
    if (target instanceof Bone) return target.getTransformNode?.() ?? null;
    if (target instanceof Node) return target;
    return null;
}

/**
 * Whether a TargetedAnimation's target falls under one of `nodeSet`
 * (resolving Bone targets to their linked TransformNode first).
 */
export function isAnimationTargetInSet(target: unknown, nodeSet: ReadonlySet<Node>): boolean {
    const resolved = resolveAnimationTargetNode(target);
    return resolved !== null && nodeSet.has(resolved);
}

/**
 * Get animation clip names embedded in the scene that target a specific object hierarchy.
 * Finds AnimationGroups whose targeted animations point at nodes/bones under `root`.
 */
export function getEmbeddedClipNames(root: TransformNode, scene: Scene): string[] {
    const underRootNodes = new Set<Node>([
        root,
        ...(root.getDescendants(false) || []),
    ]);

    const clips: string[] = [];
    for (const group of scene.animationGroups) {
        const targetsObject = group.targetedAnimations.some(ta => isAnimationTargetInSet(ta.target, underRootNodes));
        if (targetsObject && group.name) {
            clips.push(group.name);
        }
    }
    return [...new Set(clips)];
}

/**
 * Load an AssetContainer from a src path, resolving local assets via ResourceManager.
 * Handles blob URL creation/cleanup automatically.
 */
export async function loadAssetContainerFromSrc(
    src: string,
    scene: Scene,
    resources: ResourceManager
): Promise<AssetContainer> {
    let blobUrl: string | undefined;
    try {
        let url = src;
        // Try cached blob first (covers local paths and HTTP URLs with URL mapping)
        const blob = resources.getAsset(src);
        if (blob) {
            blobUrl = URL.createObjectURL(blob);
            url = blobUrl;
        }

        // Determine plugin extension from original src (blob URLs have no extension)
        const ext = src.split('.').pop()?.toLowerCase();
        const pluginExtension = ext ? `.${ext}` : ".glb";

        return await LoadAssetContainerAsync(url, scene, { pluginExtension });
    } finally {
        if (blobUrl) {
            URL.revokeObjectURL(blobUrl);
        }
    }
}

/**
 * Cache for clip names loaded from external GLB sources.
 * Keyed by src path — if a file is re-imported with the same name but different
 * content, call `clearClipNameCache()` to invalidate stale entries.
 */
const clipNameCache = new Map<string, string[]>();

/** Clear the clip-name cache (e.g. after an asset is re-imported). */
export function clearClipNameCache(): void {
    clipNameCache.clear();
}

/**
 * Get available animation clip names from an external GLB source.
 * Resolves local asset paths via ResourceManager. Results are cached per-URL.
 */
export async function getClipNamesForSrc(
    src: string,
    scene: Scene,
    resources: ResourceManager
): Promise<string[]> {
    if (clipNameCache.has(src)) {
        return clipNameCache.get(src)!;
    }

    try {
        // Load through the ResourceManager's refcounted container cache and DON'T
        // release: this keeps the parsed GLB cached so the AnimationSequence's
        // immediately-following `_loadClip` (and later re-applies/previews of the
        // same animation) reuse it instead of re-fetching+re-parsing the same GLB.
        // The held ref is a session cache — cleared with everything by
        // ResourceManager.clearAssets() on scene teardown.
        const container = await resources.getOrLoadContainer(src, scene);
        const names = container.animationGroups.map(g => g.name);

        clipNameCache.set(src, names);
        return names;
    } catch (e) {
        Logger.warn(`[AnimationUtils] Failed to load clip names from ${src}: ${String(e)}`);
        return [];
    }
}

/**
 * Extract TransformKeyframes from a Babylon.js AnimationGroup.
 *
 * Reads position, rotationQuaternion, and scaling tracks, merges keys
 * at matching frames, and converts frame numbers to seconds.
 *
 * @param group - The AnimationGroup to extract from
 * @returns Array of TransformKeyframes sorted by time
 */
export function extractKeyframesFromAnimationGroup(
    group: AnimationGroup,
): TransformKeyframe[] {
    // Collect raw keys by property name, converting frame→seconds per-track
    // using each animation's own framePerSecond. glTF-loaded animations
    // typically use fps=1 (frames ARE seconds), while programmatically-created
    // ones use the authored fps (e.g. 30).
    const positionKeys = new Map<number, Vector3>();
    const rotationKeys = new Map<number, Quaternion>();
    const scalingKeys = new Map<number, Vector3>();

    for (const ta of group.targetedAnimations) {
        const anim = ta.animation;
        const fps = anim.framePerSecond || 1;
        const prop = anim.targetProperty;
        const type = anim.dataType;

        for (const key of anim.getKeys()) {
            const time = key.frame / fps;
            if (prop === "position" && type === BabylonAnimation.ANIMATIONTYPE_VECTOR3) {
                const v = key.value as Vector3;
                positionKeys.set(time, v.clone());
            } else if (prop === "rotationQuaternion" && type === BabylonAnimation.ANIMATIONTYPE_QUATERNION) {
                const q = key.value as Quaternion;
                rotationKeys.set(time, q.clone());
            } else if (prop === "scaling" && type === BabylonAnimation.ANIMATIONTYPE_VECTOR3) {
                const v = key.value as Vector3;
                scalingKeys.set(time, v.clone());
            }
        }
    }

    // Merge all unique time values
    const allTimes = new Set<number>([
        ...positionKeys.keys(),
        ...rotationKeys.keys(),
        ...scalingKeys.keys(),
    ]);

    const keyframes: TransformKeyframe[] = [];
    for (const time of allTimes) {
        const kf: TransformKeyframe = { time };
        const pos = positionKeys.get(time);
        if (pos) kf.position = [pos.x, pos.y, pos.z];
        const rot = rotationKeys.get(time);
        if (rot) kf.rotation = [rot.x, rot.y, rot.z, rot.w];
        const scl = scalingKeys.get(time);
        if (scl) kf.scale = [scl.x, scl.y, scl.z];
        keyframes.push(kf);
    }

    keyframes.sort((a, b) => a.time - b.time);
    return keyframes;
}
